import { create } from 'zustand';
import { mockSubjects } from '../lib/mockData/subjects';
import { mockYearGroups } from '../pages/Students/MockData';
import { mockTeachers } from '../pages/Teachers/MockData';
import type { DayOfWeek, ViewConfig, ViewType } from '../types';

// Filter options types
export interface FilterOption {
  id: string;
  name: string;
}

// Active filters state
export interface ActiveFilters {
  day?: DayOfWeek;
  period?: number;
  teacherId?: string;
  yearGroupId?: string;
  roomId?: string;
  subjectId?: string;
  week?: 'A' | 'B' | 'all';
}

// Available filter options
export interface AvailableFilters {
  days: DayOfWeek[];
  periods: number[];
  teachers: FilterOption[];
  yearGroups: FilterOption[];
  rooms: FilterOption[];
  subjects: FilterOption[];
  weeks: Array<{ id: 'A' | 'B' | 'all'; name: string }>;
}

interface FilterState {
  // View configuration
  viewConfig: ViewConfig;
  setViewConfig: (config: Partial<ViewConfig>) => void;
  setViewType: (viewType: ViewType) => void;

  // Active filters
  activeFilters: ActiveFilters;
  setActiveFilters: (filters: ActiveFilters) => void;
  updateFilter: <K extends keyof ActiveFilters>(key: K, value: ActiveFilters[K]) => void;
  clearFilters: () => void;
  clearFilter: (key: keyof ActiveFilters) => void;

  // Available filter options (loaded from mock data)
  availableFilters: AvailableFilters;
  loadAvailableFilters: () => void;

  // Quick filter presets
  setTeacherView: (teacherId: string) => void;
  setYearGroupView: (yearGroupId: string) => void;
  setRoomView: (roomId: string) => void;
  setDayView: (day: DayOfWeek) => void;
  setWholeSchoolView: () => void;

  // Utility
  getActiveFilterCount: () => number;
  hasActiveFilters: () => boolean;
}

// Default days of the week
const defaultDays: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

// Default periods (5 periods per day)
const defaultPeriods = [1, 2, 3, 4, 5];

// Generate room options from school store mock data
const generateRoomOptions = (): FilterOption[] => {
  // Room data based on common UK school structure
  const rooms: FilterOption[] = [];
  const floors = ['Ground', '1', '2'];

  // General classrooms
  floors.forEach((floor) => {
    for (let i = 1; i <= 4; i++) {
      rooms.push({
        id: `room-${floor === 'Ground' ? 'G' : floor}${String(i).padStart(2, '0')}`,
        name: `Room ${floor === 'Ground' ? 'G' : floor}${String(i).padStart(2, '0')}`,
      });
    }
  });

  // Science labs
  for (let i = 1; i <= 4; i++) {
    rooms.push({ id: `room-sci-lab-${i}`, name: `Science Lab ${i}` });
  }

  // ICT suites
  for (let i = 1; i <= 2; i++) {
    rooms.push({ id: `room-ict-${i}`, name: `ICT Suite ${i}` });
  }

  // Specialist rooms
  rooms.push({ id: 'room-art-1', name: 'Art Room 1' });
  rooms.push({ id: 'room-art-2', name: 'Art Room 2' });
  rooms.push({ id: 'room-music-1', name: 'Music Room 1' });
  rooms.push({ id: 'room-drama-1', name: 'Drama Studio' });
  rooms.push({ id: 'room-dt-1', name: 'Design Technology 1' });
  rooms.push({ id: 'room-food-tech', name: 'Food Technology' });
  rooms.push({ id: 'room-gym', name: 'Gymnasium' });
  rooms.push({ id: 'room-sports-hall', name: 'Sports Hall' });
  rooms.push({ id: 'room-library', name: 'Library' });
  rooms.push({ id: 'room-hall', name: 'Main Hall' });

  return rooms;
};

// Initialize available filters from mock data
const initializeAvailableFilters = (): AvailableFilters => {
  return {
    days: defaultDays,
    periods: defaultPeriods,
    teachers: mockTeachers.map((t) => ({
      id: t.id,
      name: `${t.firstName} ${t.lastName}`,
    })),
    yearGroups: mockYearGroups.map((yg) => ({
      id: yg.id,
      name: yg.name,
    })),
    rooms: generateRoomOptions(),
    subjects: mockSubjects.map((s) => ({
      id: s.id,
      name: s.name,
    })),
    weeks: [
      { id: 'all', name: 'All Weeks' },
      { id: 'A', name: 'Week A' },
      { id: 'B', name: 'Week B' },
    ],
  };
};

// Default view configuration
const defaultViewConfig: ViewConfig = {
  viewType: 'whole-school',
  filters: [],
  showConflicts: true,
  showFreePeriods: true,
  colorBy: 'subject',
};

// Default empty filters
const defaultActiveFilters: ActiveFilters = {
  week: 'all',
};

export const useFilterStore = create<FilterState>((set, get) => ({
  // Initial state
  viewConfig: defaultViewConfig,
  activeFilters: defaultActiveFilters,
  availableFilters: initializeAvailableFilters(),

  // View configuration setters
  setViewConfig: (config) =>
    set((state) => ({
      viewConfig: { ...state.viewConfig, ...config },
    })),

  setViewType: (viewType) =>
    set((state) => ({
      viewConfig: { ...state.viewConfig, viewType },
    })),

  // Active filters setters
  setActiveFilters: (filters) =>
    set(() => ({
      activeFilters: filters,
    })),

  updateFilter: (key, value) =>
    set((state) => ({
      activeFilters: { ...state.activeFilters, [key]: value },
    })),

  clearFilters: () =>
    set(() => ({
      activeFilters: { week: 'all' },
    })),

  clearFilter: (key) =>
    set((state) => {
      const newFilters = { ...state.activeFilters };
      delete newFilters[key];
      return { activeFilters: newFilters };
    }),

  // Load available filters from mock data
  loadAvailableFilters: () => {
    set(() => ({
      availableFilters: initializeAvailableFilters(),
    }));
  },

  // Quick filter presets
  setTeacherView: (teacherId) =>
    set((state) => ({
      viewConfig: { ...state.viewConfig, viewType: 'teacher' },
      activeFilters: { ...state.activeFilters, teacherId, yearGroupId: undefined, roomId: undefined },
    })),

  setYearGroupView: (yearGroupId) =>
    set((state) => ({
      viewConfig: { ...state.viewConfig, viewType: 'year-group' },
      activeFilters: { ...state.activeFilters, yearGroupId, teacherId: undefined, roomId: undefined },
    })),

  setRoomView: (roomId) =>
    set((state) => ({
      viewConfig: { ...state.viewConfig, viewType: 'room' },
      activeFilters: { ...state.activeFilters, roomId, teacherId: undefined, yearGroupId: undefined },
    })),

  setDayView: (day) =>
    set((state) => ({
      viewConfig: { ...state.viewConfig, viewType: 'day' },
      activeFilters: { ...state.activeFilters, day },
    })),

  setWholeSchoolView: () =>
    set(() => ({
      viewConfig: { ...defaultViewConfig, viewType: 'whole-school' },
      activeFilters: { week: 'all' },
    })),

  // Utility functions
  getActiveFilterCount: () => {
    const { activeFilters } = get();
    return Object.entries(activeFilters).filter(
      ([key, value]) => value !== undefined && value !== null && (key !== 'week' || value !== 'all'),
    ).length;
  },

  hasActiveFilters: () => {
    return get().getActiveFilterCount() > 0;
  },
}));

